import type Highcharts from 'highcharts/es-modules/masters/highcharts.src.js';
import type {
  ChartTemplate,
  ChartThemeModeTokens,
  ChartThemePayload,
  ColorVisionMode,
  ThemeAppearance,
} from '@lognai/plugin-analytics';

export const DEFAULT_UI_CHART_THEME: ChartThemePayload = {
  schemaVersion: 1,
  light: {
    palette: {
      categorical: ['#516b91', '#59c4e6', '#edafda', '#93b7e3', '#a5e7f0', '#cbb0e3', '#3d7fa6', '#9a7fbd'],
      sequential: { low: '#e8f1fa', mid: '#93b7e3', high: '#516b91' },
      diverging: { negative: '#dc2626', neutral: '#e2e8f0', positive: '#10b981' },
      semantic: { positive: '#10b981', warning: '#f59e0b', critical: '#dc2626', neutral: '#64748b' },
      comparison: { current: '#516b91', previous: '#94a3b8', target: '#59c4e6' },
    },
    surface: { chartBackground: '#ffffff', plotBackground: '#ffffff', text: '#0f172a', mutedText: '#475569', axis: '#94a3b8', grid: '#e2e8f0', border: '#cbd5e1', tooltipBackground: '#0f172a', tooltipText: '#ffffff' },
    typography: { fontStack: 'app', scale: 'standard', labelWeight: 500 },
    series: { fillStyle: 'soft-gradient', gradientDirection: 'vertical', lineWidth: 3, marker: 'circle', opacity: 0.9, borderWidth: 0, borderRadius: 4, spacing: 8 },
    components: { legendPosition: 'bottom', labelDensity: 'balanced', gridDensity: 'subtle', tooltipStyle: 'elevated' },
    interaction: { hoverBrightness: 0.06, inactiveOpacity: 0.35, selectionWidth: 3, motion: 'standard' },
  },
  dark: {
    palette: {
      categorical: ['#516b91', '#59c4e6', '#edafda', '#93b7e3', '#a5e7f0', '#cbb0e3', '#3d7fa6', '#9a7fbd'],
      sequential: { low: '#26374f', mid: '#59c4e6', high: '#a5e7f0' },
      diverging: { negative: '#f87171', neutral: '#475569', positive: '#34d399' },
      semantic: { positive: '#34d399', warning: '#fbbf24', critical: '#f87171', neutral: '#94a3b8' },
      comparison: { current: '#93b7e3', previous: '#94a3b8', target: '#59c4e6' },
    },
    surface: { chartBackground: '#1e293b', plotBackground: '#1e293b', text: '#f8fafc', mutedText: '#cbd5e1', axis: '#64748b', grid: '#334155', border: '#475569', tooltipBackground: '#f8fafc', tooltipText: '#0f172a' },
    typography: { fontStack: 'app', scale: 'standard', labelWeight: 500 },
    series: { fillStyle: 'soft-gradient', gradientDirection: 'vertical', lineWidth: 3, marker: 'circle', opacity: 0.9, borderWidth: 0, borderRadius: 4, spacing: 8 },
    components: { legendPosition: 'bottom', labelDensity: 'balanced', gridDensity: 'subtle', tooltipStyle: 'elevated' },
    interaction: { hoverBrightness: 0.06, inactiveOpacity: 0.35, selectionWidth: 3, motion: 'standard' },
  },
  chartOverrides: {},
  accessibility: { minimumContrast: 'AA', patterns: 'auto', distinguishabilityThreshold: 10, respectReducedMotion: true },
};

const CVD_MATRICES: Record<Exclude<ColorVisionMode, 'normal'>, number[]> = {
  protanopia: [0.567, 0.433, 0, 0.558, 0.442, 0, 0, 0.242, 0.758],
  deuteranopia: [0.625, 0.375, 0, 0.7, 0.3, 0, 0, 0.3, 0.7],
  tritanopia: [0.95, 0.05, 0, 0, 0.433, 0.567, 0, 0.475, 0.525],
};

export const simulateThemeHex = (hex: string, mode: ColorVisionMode): string => {
  if (mode === 'normal' || !/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const rgb = [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16));
  const matrix = CVD_MATRICES[mode];
  const next = [0, 1, 2].map((row) => Math.max(0, Math.min(255, Math.round(
    rgb[0]! * matrix[row * 3]! + rgb[1]! * matrix[row * 3 + 1]! + rgb[2]! * matrix[row * 3 + 2]!,
  ))));
  return `#${next.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
};

const FONT_STACKS = {
  app: 'Inter, ui-sans-serif, system-ui, sans-serif',
  'system-sans': 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  'presentation-serif': 'Georgia, Cambria, Times New Roman, serif',
} as const;

const FONT_SIZES = {
  compact: { axis: '10px', legend: '10px', data: '9px' },
  standard: { axis: '11px', legend: '11px', data: '10px' },
  large: { axis: '12px', legend: '12px', data: '11px' },
} as const;

export interface CompiledChartTheme {
  tokens: ChartThemeModeTokens;
  colors: string[];
  fontFamily: string;
  fontSizes: (typeof FONT_SIZES)[keyof typeof FONT_SIZES];
  legend: Highcharts.LegendOptions;
  animation: false | Highcharts.AnimationOptionsObject;
  dataLabelsEnabled: boolean;
  gridLineWidth: number;
  patternMode: ChartThemePayload['accessibility']['patterns'];
}

export const compileChartTheme = (
  theme: ChartThemePayload,
  appearance: ThemeAppearance,
  template: ChartTemplate,
  reducedMotion = false,
  colorVisionMode: ColorVisionMode = 'normal',
): CompiledChartTheme => {
  const base = theme[appearance];
  const override = theme.chartOverrides[template] ?? {};
  const rawTokens: ChartThemeModeTokens = {
    ...base,
    series: { ...base.series, ...override },
    components: {
      ...base.components,
      labelDensity: override.labelDensity ?? base.components.labelDensity,
      legendPosition: override.legendPosition ?? base.components.legendPosition,
    },
  };
  const map = (color: string) => simulateThemeHex(color, colorVisionMode);
  const tokens: ChartThemeModeTokens = colorVisionMode === 'normal' ? rawTokens : {
    ...rawTokens,
    palette: {
      categorical: rawTokens.palette.categorical.map(map),
      sequential: { low: map(rawTokens.palette.sequential.low), mid: map(rawTokens.palette.sequential.mid), high: map(rawTokens.palette.sequential.high) },
      diverging: { negative: map(rawTokens.palette.diverging.negative), neutral: map(rawTokens.palette.diverging.neutral), positive: map(rawTokens.palette.diverging.positive) },
      semantic: { positive: map(rawTokens.palette.semantic.positive), warning: map(rawTokens.palette.semantic.warning), critical: map(rawTokens.palette.semantic.critical), neutral: map(rawTokens.palette.semantic.neutral) },
      comparison: { current: map(rawTokens.palette.comparison.current), previous: map(rawTokens.palette.comparison.previous), target: map(rawTokens.palette.comparison.target) },
    },
  };
  const legendPosition = tokens.components.legendPosition;
  const duration = reducedMotion || tokens.interaction.motion === 'reduced'
    ? 0
    : tokens.interaction.motion === 'expressive' ? 700 : 450;
  return {
    tokens,
    colors: tokens.palette.categorical,
    fontFamily: FONT_STACKS[tokens.typography.fontStack],
    fontSizes: FONT_SIZES[tokens.typography.scale],
    legend: {
      enabled: legendPosition !== 'hidden',
      align: legendPosition === 'right' ? 'right' : 'center',
      verticalAlign: legendPosition === 'top' ? 'top' : legendPosition === 'right' ? 'middle' : 'bottom',
      layout: legendPosition === 'right' ? 'vertical' : 'horizontal',
    },
    animation: duration ? { duration } : false,
    dataLabelsEnabled: tokens.components.labelDensity === 'detailed',
    gridLineWidth: tokens.components.gridDensity === 'none' ? 0 : 1,
    patternMode: theme.accessibility.patterns,
  };
};

export const themedGradient = (
  HighchartsInstance: typeof Highcharts,
  from: string,
  background: string,
  direction: ChartThemeModeTokens['series']['gradientDirection'],
  strength: ChartThemeModeTokens['series']['fillStyle'],
): Highcharts.GradientColorObject | string => {
  if (strength === 'solid') return from;
  const opacity = strength === 'strong-gradient' ? 0.24 : 0.08;
  const end = String(HighchartsInstance.color(background).brighten(strength === 'strong-gradient' ? -0.04 : 0).setOpacity(opacity).get());
  if (direction === 'radial') return { radialGradient: { cx: 0.45, cy: 0.35, r: 0.8 }, stops: [[0, from], [1, end]] };
  return {
    linearGradient: direction === 'horizontal'
      ? { x1: 0, y1: 0, x2: 1, y2: 0 }
      : { x1: 0, y1: 0, x2: 0, y2: 1 },
    stops: [[0, from], [1, end]],
  };
};

const PATHS = ['M 0 0 L 8 8', 'M 8 0 L 0 8', 'M 0 4 L 8 4', 'M 4 0 L 4 8'];

export const themedPointColor = (
  color: string,
  index: number,
  patternMode: ChartThemePayload['accessibility']['patterns'],
): Highcharts.ColorType => {
  if (patternMode !== 'always') return color;
  return {
    pattern: {
      width: 8,
      height: 8,
      color,
      backgroundColor: color,
      opacity: 0.36,
      path: { d: PATHS[index % PATHS.length]!, stroke: '#ffffff', strokeWidth: 1.2 },
    },
  };
};
